import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { isEmpty } from "lodash";
import navigatorService from "../../services/navigatorService";
import { setIsChatDrawerOpen } from "../../reducers/common-reducer";
import chatService from "../../services/chatService";
import { ChatBox } from "./ChatComponents";
import useLanguageChange from "../../hooks/useLanguageChange";
import { resetUnreadChatMsgCount, setIsTranslationPanelOpen } from "../../reducers/chat-reducer";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { isSilentObserverAvailable } from "../../shared/function";
import DownloadChatNotSupportedModal from "../CommonModals/DownloadChatNotSupportedModal";
import TranslationSettingsPanel from "./TranslationSettingsPanel";
import AgentSendLanguageSelector from "./AgentSendLanguageSelector";

const ChatDrawer = () => {
    const dispatch = useDispatch();

    const { isChatDrawerOpen } = useSelector((state) => state.commonReducer);
    const { chatList, isTranslationPanelOpen, agentSendLanguage } = useSelector((state) => state.chatReducer);
    const { endCallData } = useSelector((state) => state.endCall);
    const inputRef = useRef(null);

    const localisedValues = useLanguageChange();
    const [message, setMessage] = useState("");
    const [isDownloadNotAvailable, setIsDownloadNotAvailable] = useState(false);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        /* Clear text message input after participant disconnect call */
        if (endCallData) {
            setMessage("");
        }
    }, [endCallData]);

    const handleCloseChatDrawer = () => {
        dispatch(setIsChatDrawerOpen(false));
        dispatch(resetUnreadChatMsgCount());
        dispatch(setIsTranslationPanelOpen(false));
    };

    const handleToggleTranslationPanel = () => {
        dispatch(setIsTranslationPanelOpen(!isTranslationPanelOpen));
    };

    const handleOnKeyDown = (e) => {
        if (e && (e.key === "Enter" || e.which === 13) && !e.shiftKey) {
            handleSendMessage(e);
        }
    };

    const handleOnInputChange = (e) => {
        let msgVal = e.target.value;
        setMessage(msgVal);
        if (isEmpty(msgVal)) {
            inputRef.current.style.height = "36px";
        }
        handleAutoResizeTextArea();
    };

    /** Return true if scroll at bottom */
    const isScrollAtBottom = function () {
        const chatSidebarBody = document.querySelector(".chat-sidebar-body");
        return Math.ceil(chatSidebarBody.scrollTop + chatSidebarBody.offsetHeight) >= chatSidebarBody.scrollHeight;
    };

    /** Handling auto height of textarea for message in chat */
    const handleAutoResizeTextArea = () => {
        const chatTextArea = inputRef.current;
        const { scrollHeight } = chatTextArea;

        if (scrollHeight >= 112) {
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }

        const isNeedToScroll = scrollHeight > 36 && isScrollAtBottom();

        chatTextArea.style.height = "36px";
        chatTextArea.style.height = `${scrollHeight}px`;

        if (isNeedToScroll) chatService.moveScrollToBottom();
    };

    const handleSendMessage = async (e) => {
        const trimmedMessage = message.trim();
        // in mobile device, do not send message while clicking on enter
        if (isEmpty(trimmedMessage) || (navigatorService.isMobileDeviceOrIpadOs() && (e?.key === "Enter" || e.which === 13))) return;

        // Prevent duplicate sends while message is being processed
        if (isSending) return;

        if (e?.key === "Enter" || e.which === 13) {
            e.preventDefault();
        }

        setIsSending(true);
        try {
            // Agent uses translation when sending messages
            if (GLOBAL_MEETING_DATA.isAgent && agentSendLanguage) {
                await chatService.sendMessageWithTranslation(trimmedMessage, agentSendLanguage);
            } else {
                chatService.sendMessage(trimmedMessage);
            }

            setMessage("");
            inputRef.current.style.height = "36px";
            chatService.moveScrollToBottom();
        } finally {
            setIsSending(false);
        }
    };

    // download .txt file of chat during call is running
    const handleSaveChat = () => {
        // as we support iOS above 13 OS version, so we remove isiOSBelow13 condition
        const chatMsgList = [...chatList].reverse();
        const textToBLOB = chatService.convertDataToBlob(chatMsgList);
        const filename = chatService.createFileName("chat"); // The file to save the data.
        let newLink = document.createElement("a");
        newLink.download = filename;
        if (window.webkitURL != null) {
            newLink.href = window.webkitURL.createObjectURL(textToBLOB);
        } else {
            newLink.href = window.URL.createObjectURL(textToBLOB);
            newLink.style.display = "none";
            newLink.target = "_blank";
            document.body.appendChild(newLink);
        }
        newLink.click();
    };

    return (
        <>
            <div className={clsx("sidebar left-sidebar", isChatDrawerOpen && "active")} id='chat-sidebar'>
                <div className='close-in-mobile' onClick={handleCloseChatDrawer} onKeyDown={() => {}}></div>
                <div className='sidebar-in'>
                    <div className='sidebar-head'>
                        <div className='sidebar-head-title'>
                            <div className='title'>{localisedValues["chat"]}</div>
                            <div className='custom-tooltip right-tooltip'>
                                <div className='custom-tooltip-icon'>
                                    <span className='icon-info'></span>
                                </div>
                                <div className='custom-tooltip-content'>
                                    <div className='custom-tooltip-in'>
                                        <p>{localisedValues["chat_info"]}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='sidebar-head-right'>
                            {/* Translation Button - Agent only */}
                            {GLOBAL_MEETING_DATA.isAgent && (
                                <div className='chat-translate-button custom-tooltip center-tooltip tooltip-auto-width'>
                                    <button
                                        className={clsx("btn btn-icon-only translate-btn", isTranslationPanelOpen && "active")}
                                        onClick={handleToggleTranslationPanel}
                                        data-testid='chat-translate-btn'
                                    >
                                        <span className='btn-icon translate-icon-text'>{`A\u6587`}</span>
                                    </button>
                                    <div className='custom-tooltip-content text-nowrap'>
                                        <div className='custom-tooltip-in'>
                                            <p>{localisedValues["translate"] || "Translate"}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {GLOBAL_MEETING_DATA.isAgent && chatList.length > 0 && (
                                <div className='chat-download-button custom-tooltip center-tooltip tooltip-auto-width m-hide'>
                                    <button className='btn btn-primary download-btn' onClick={handleSaveChat}>
                                        <span className='btn-icon icon-download'></span>
                                    </button>
                                    <div className='custom-tooltip-content text-nowrap'>
                                        <div className='custom-tooltip-in'>
                                            <p>{localisedValues["download_key"]}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className='sidebar-close'>
                                <button className='btn close-sidebar-button' onClick={handleCloseChatDrawer}>
                                    <span className='for-icon icon-close-light'></span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className='sidebar-body chat-sidebar-body custom-scroll'>
                        {chatList?.map((chat, index) => {
                            return (
                                <ChatBox
                                    chat={chat}
                                    key={"chatlist_" + chat.timestamp + "_" + index}
                                    isSender={chat.identity == CALL_DATA.localIdentity}
                                />
                            );
                        })}
                    </div>

                    {/* Translation Settings Panel - Agent only */}
                    {GLOBAL_MEETING_DATA.isAgent && <TranslationSettingsPanel />}

                    {!isSilentObserverAvailable() && (
                        <div className='sidebar-foot'>
                            {/* Agent Send Language Selector */}
                            {GLOBAL_MEETING_DATA.isAgent && <AgentSendLanguageSelector />}
                            <div className='chat-message-form'>
                                <div className='swme-form-group swme-tabbar-form-group'>
                                    <div className='swme-form-group-in'>
                                        <textarea
                                            ref={inputRef}
                                            className='swme-form-control custom-scroll-thin'
                                            placeholder={localisedValues["chat_input_placeholder"]}
                                            value={message}
                                            onChange={handleOnInputChange}
                                            onKeyDown={handleOnKeyDown}
                                            autoComplete='off'
                                            required
                                            id='chat_input'
                                            maxLength={9999}
                                            style={{ fontSize: PublicVariables.isIOSDevice ? "16px" : "" }}
                                        ></textarea>
                                        <button
                                            className={clsx("btn send-button", isSending && "is-sending")}
                                            data-testid='chatSendIconBtn'
                                            onClick={handleSendMessage}
                                            disabled={isEmpty(message.trim()) || isSending}
                                        >
                                            {isSending ? (
                                                <span className='send-btn-loader'>
                                                    <span className='round-loader'></span>
                                                </span>
                                            ) : (
                                                <span className='btn-icon icon-send'></span>
                                            )}
                                        </button>
                                        <span className='for-bg'></span>
                                        <span className='for-border'></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <DownloadChatNotSupportedModal
                isDownloadNotAvailable={isDownloadNotAvailable}
                handleCloseModal={() => {
                    setIsDownloadNotAvailable(false);
                }}
            />
        </>
    );
};

export default ChatDrawer;
